import { StarRounded } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  Rating,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { get } from 'lodash';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { getUser } from '@app/adapter/user-service';
import { userAuthInfoSelector } from '@app/domain/app';
import { Review } from '@app/types/review';
import { User } from '@app/types/user';
import { isError } from '@app/utils/error';

const rates = [
  { key: 'quality', label: '医療の質' },
  { key: 'speed', label: '診察の早さ' },
  { key: 'reception', label: '接遇・清潔感' },
  { key: 'communication', label: '職員間コミュニケーション' },
  { key: 'followRule', label: 'クリニック方針の遵守' },
  { key: 'attendance', label: '勤怠（遅刻・遅延）' },
] as const;

interface ReviewListProps {
  items: Review[];
}

export function ReviewList({ items }: ReviewListProps): ReactElement {
  const theme = useTheme();
  const userInfo = useRecoilValue(userAuthInfoSelector);
  const [user, setUser] = useState<User>();

  const fetchUser = useCallback(async () => {
    if (!userInfo) return;
    try {
      const response = await getUser(
        userInfo.userId,
        userInfo.accessToken,
        userInfo.fingerPrint
      );
      setUser(response.data);
    } catch (e) {
      if (isError(e)) {
        throw new Error(e.message);
      }
    }
  }, [userInfo]);

  useEffect(() => {
    void fetchUser();
    // eslint-disable-next-line
  }, []);

  const getRate = (key: string): number => {
    if (!user?.reviewStats) return 0;
    return Math.floor(get(user.reviewStats, key, 0) * 10) / 10;
  };

  return (
    <Stack spacing={{ xs: 3 }} sx={{ px: 2 }}>
      {items.length > 2 ? (
        <>
          <Card
            sx={{
              backgroundColor: theme.palette.neutral.greyLight,
              textAlign: 'center',
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Typography variant="body2">総合評価</Typography>
                <Typography variant="h5">{getRate('averageRate')}</Typography>
              </Stack>
              <Box>
                <Rating
                  defaultValue={getRate('averageRate')}
                  precision={0.1}
                  icon={<StarRounded fontSize="inherit" />}
                  emptyIcon={<StarRounded fontSize="inherit" />}
                  readOnly
                  sx={{ fontSize: '2.75rem' }}
                />
              </Box>
            </CardContent>
          </Card>
          <Stack>
            {rates.map((rate, index) => {
              return (
                <Grid
                  key={index}
                  container
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Grid item>
                    <Typography variant="body2">
                      {rates.find((r) => r.key === rate.key)?.label}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Stack direction="row" alignItems="center">
                      <Rating
                        defaultValue={getRate(`averageSubRates.${rate.key}`)}
                        precision={0.1}
                        icon={<StarRounded fontSize="inherit" />}
                        emptyIcon={<StarRounded fontSize="inherit" />}
                        readOnly
                      />
                      <Typography textAlign="right" sx={{ width: '28px' }}>
                        {getRate(`averageSubRates.${rate.key}`)}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              );
            })}
          </Stack>
          <Stack spacing={3} divider={<Divider flexItem />} pb={3}>
            <Typography variant="body2">コメント一覧</Typography>
            {items.map((item, index) => (
              <Box key={index}>
                <Typography>{item.comment}</Typography>
              </Box>
            ))}
          </Stack>
        </>
      ) : (
        <Card
          sx={{
            backgroundColor: theme.palette.neutral.greyLight,
            mb: 2,
            textAlign: 'center',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography>評価はまだありません</Typography>
            <Typography variant="body2" component="div" mt={1}>
              <Box>勤務したクリニックからの評価が</Box>
              <Box>3件以上になると表示されます</Box>
            </Typography>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
